import { LocationTracker } from "@repo/helpers";
import { keyPair } from "@repo/models";
import { createContext, ReactNode, useContext } from "react";

import { CustomComponentProps } from "../dynamicComponent/types";
import { MappedComponent } from "../models/mappedComponent";

/**
 * Holds all items needed specifically for a dynamic component
 * instead of just passing props thru
 * Intended for efficiency
 */
export interface ComponentContextType {
	valueChanged: (value: any, id: string, parentComponentName?: string, dontEdit?: boolean) => Promise<void>;
	valuesChanged?: (values: keyPair[], parentComponentName?: string, dontEdit?: boolean) => Promise<void>;
	setCopiedFrom?: (component: MappedComponent) => Promise<void>;

	//Function specific to file components
	deleteUploadedFile?: (id: number, propName: string) => Promise<void>;
	getUploadedFile?: (id: number) => void;

	/** Fn to return custom components (not defined in DynamicComponent switch) */
	getCustomComponent?: (props: CustomComponentProps) => JSX.Element;
	getComponentAddon?: (propertyName: string) => JSX.Element;
	getComponentAddonPosition?: (propertyName: string) => "top" | "bottom" | "left" | "right";
	customFunctions?: (name: string, other?: any) => (params?: any) => Promise<any>;
	locationTracker?: LocationTracker;
}

const defaultContextValues = {
	valueChanged: undefined,
	valuesChanged: undefined,
	setCopiedFrom: undefined,
	deleteUploadedFile: undefined,
	getUploadedFile: undefined,
	getCustomComponent: undefined,
	getComponentAddon: undefined,
	getComponentAddonPosition: undefined,
	customFunctions: undefined,
	locationTracker: undefined,
} as ComponentContextType;

interface ComponentProps {
	children: ReactNode;
	valueChanged: (value: any, id: string, parentComponentName?: string, dontEdit?: boolean) => Promise<void>;
	valuesChanged?: (values: keyPair[], parentComponentName?: string, dontEdit?: boolean) => Promise<void>;
	setCopiedFrom?: (component: MappedComponent) => Promise<void>;
	deleteUploadedFile?: (id: number, propName: string) => Promise<void>;
	getUploadedFile?: (id: number) => void;
	getCustomComponent?: (props: CustomComponentProps) => JSX.Element;
	getComponentAddon?: (propertyName: string) => JSX.Element;
	getComponentAddonPosition?: (propertyName: string) => "top" | "bottom" | "left" | "right";
	customFunctions?: (name: string, other?: any) => (params?: any) => Promise<any>;
	locationTracker?: LocationTracker;
}

export const ComponentContext = createContext<ComponentContextType>(defaultContextValues);
export const useComponentContext = () => useContext(ComponentContext);
export function ComponentProvider(props: ComponentProps) {
	const {
		children,
		valueChanged,
		valuesChanged,
		setCopiedFrom,
		deleteUploadedFile,
		getUploadedFile,
		getCustomComponent,
		getComponentAddon,
		getComponentAddonPosition,
		customFunctions,
		locationTracker,
	} = props;
	return (
		<ComponentContext.Provider
			value={{
				valueChanged,
				valuesChanged,
				setCopiedFrom,
				deleteUploadedFile,
				getUploadedFile,
				getCustomComponent,
				getComponentAddon,
				getComponentAddonPosition,
				customFunctions,
				locationTracker,
			}}
		>
			{children}
		</ComponentContext.Provider>
	);
}
